import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { AlertTriangle, Check, MessageCircle } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Switch } from '@/pages/catalog/shared';
import { OrderNotifyEvent, OrderNotifySettings, ordersService } from '@/services/orders.service';

const EVENT_LABEL: Record<OrderNotifyEvent, { title: string; hint: string }> = {
  placed: { title: 'Order received', hint: 'Right after it is placed' },
  preparing: { title: 'Being prepared', hint: 'When the kitchen starts' },
  ready: { title: 'Ready', hint: 'Ready to serve or collect' },
  out_for_delivery: { title: 'On its way', hint: 'Delivery orders only' },
  completed: { title: 'Completed', hint: 'A thank-you once it is done' },
  cancelled: { title: 'Cancelled', hint: 'If the order is cancelled' },
};

/**
 * WhatsApp order updates. Off until an admin turns it on; needs an approved
 * template because most updates land outside the 24-hour window.
 */
export default function OrderNotifyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [data, setData] = useState<OrderNotifySettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [templateUid, setTemplateUid] = useState<string | null>(null);
  const [events, setEvents] = useState<OrderNotifyEvent[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    ordersService.notifySettings().then((d) => {
      setData(d); setEnabled(d.enabled); setTemplateUid(d.templateUid); setEvents(d.events);
    }).catch(() => onClose());
  }, [open, onClose]);

  const usable = data?.templates.filter((t) => !t.problem) ?? [];
  const chosen = data?.templates.find((t) => t.templateUid === templateUid) ?? null;

  const save = async () => {
    setBusy(true);
    try {
      const d = await ordersService.updateNotifySettings({ enabled, templateUid, events });
      setData(d);
      toast.success(d.enabled ? 'WhatsApp order updates are on' : 'WhatsApp order updates saved (off)');
      onClose();
    } catch (e: any) { if (!e?.response) toast.error('Could not save.'); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="WhatsApp order updates" widthClass="max-w-xl">
      {!data ? <div className="skeleton h-64 rounded-xl" /> : (
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-surface-alt/60 p-3.5">
            <div className="flex gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success"><MessageCircle size={17} /></span>
              <div>
                <div className="text-sm font-semibold text-text-primary">Message customers as their order moves</div>
                <p className="mt-0.5 text-xs text-text-muted">Sent to the phone number on the order, from your WhatsApp number, with the template you pick. Orders without a phone number are skipped.</p>
              </div>
            </div>
            <Switch checked={enabled} onChange={setEnabled} label="Send WhatsApp order updates" disabled={!data.whatsappConnected} />
          </div>

          {!data.whatsappConnected && (
            <Notice>Connect a WhatsApp number first — <Link to="/whatsapp-accounts" className="font-semibold text-primary hover:underline">WhatsApp accounts</Link>.</Notice>
          )}

          <section>
            <div className="mb-2 text-[13px] font-medium text-text-secondary">Template</div>
            {usable.length === 0 ? (
              <Notice>
                No approved template fits yet. Create a <strong>utility</strong> template with up to 5 variables — e.g. “Hi {'{{customer_name}}'}, your order {'{{order_number}}'} is {'{{status}}'}.” — under{' '}
                <Link to="/templates" className="font-semibold text-primary hover:underline">Templates</Link> and wait for Meta to approve it.
              </Notice>
            ) : (
              <div className="space-y-2">
                {usable.map((t) => (
                  <button key={t.templateUid} type="button" onClick={() => setTemplateUid(t.templateUid)} aria-pressed={templateUid === t.templateUid}
                    className={clsx('w-full rounded-xl border p-3 text-left transition-colors',
                      templateUid === t.templateUid ? 'border-primary bg-primary-soft/50' : 'border-border hover:border-border-strong')}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs font-semibold text-text-primary">{t.name} <span className="font-sans font-normal text-text-muted">· {t.language}</span></span>
                      {templateUid === t.templateUid && <Check size={14} className="text-primary" />}
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-text-secondary">{t.body}</p>
                  </button>
                ))}
              </div>
            )}
            {chosen && chosen.variables.length > 0 && (
              <p className="mt-2 text-2xs text-text-muted">
                Variables are filled by name — customer name, order number, status, total, business name — or in that order when the names don’t say.
              </p>
            )}
          </section>

          <section>
            <div className="mb-2 text-[13px] font-medium text-text-secondary">Send when the order is…</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {data.availableEvents.map((ev) => {
                const on = events.includes(ev);
                return (
                  <label key={ev} className={clsx('flex cursor-pointer items-start gap-2.5 rounded-xl border p-3 transition-colors', on ? 'border-primary/50 bg-primary-soft/40' : 'border-border hover:border-border-strong')}>
                    <input type="checkbox" className="mt-0.5" checked={on}
                      onChange={(e) => setEvents((cur) => (e.target.checked ? [...cur, ev] : cur.filter((x) => x !== ev)))} />
                    <span>
                      <span className="block text-sm font-medium text-text-primary">{EVENT_LABEL[ev].title}</span>
                      <span className="block text-2xs text-text-muted">{EVENT_LABEL[ev].hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button loading={busy} onClick={save} disabled={enabled && (!templateUid || events.length === 0)}>Save</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-warning/40 bg-warning-soft px-3.5 py-2.5 text-xs text-text-secondary">
      <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
      <span>{children}</span>
    </div>
  );
}
